import { useCallback, useEffect, useRef, useState } from 'react';
import type { DenoiseChain, DenoiseKind } from './socketDenoise';

// ─── Microphone → SalesPro socket ──────────────────────────────────────────
//
// The customer's voice goes up the same socket the narration comes down, as raw
// PCM16 mono @ 16 kHz. The backend bridges it to the LiveKit agent server-side,
// so the browser never joins a room itself.
//
// Deliberately thin compared with the pipeline this replaces: no Silero VAD, no
// client-side STT. Turn detection and transcription happen server-side. The one
// thing that does run here is noise removal (see socketDenoise.ts): this path has
// no LiveKit room and so no server-side noise cancellation, and the browser is the
// only place the room noise and stray voices can be taken out before the agent
// hears them.

/** 20 ms of PCM16 @ 16 kHz — what the capture worklet emits per message. */
const WORKLET_URL = `${process.env.PUBLIC_URL || ''}/pcm-downsampler.worklet.js`;

/** RNNoise and DeepFilterNet3 both need 48 kHz; must match DENOISE_SAMPLE_RATE in socketDenoise.ts. */
const CAPTURE_SAMPLE_RATE = 48000;

export type MicStatus = 'idle' | 'starting' | 'live' | 'denied' | 'error';

export interface UseSocketMicOptions {
  /** Hand one 20 ms PCM16 frame to the transport. Return false if it could not be sent. */
  sendFrame: (frame: ArrayBuffer) => boolean;
  /** While true the capture graph stays up but frames are dropped, so unmuting is instant. */
  isMuted: boolean;
}

/** getUserMedia refusals — a retry only helps once the customer changes their mind. */
const isPermissionError = (error: unknown): boolean => {
  const name = (error as { name?: string } | undefined)?.name;
  return name === 'NotAllowedError' || name === 'NotFoundError' || name === 'NotReadableError' || name === 'SecurityError';
};

export function useSocketMic({ sendFrame, isMuted }: UseSocketMicOptions) {
  const [status, setStatus] = useState<MicStatus>('idle');
  const [micError, setMicError] = useState<string | null>(null);
  // Surfaced so the "You" orb can draw the customer's own level from the live track.
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletRef = useRef<AudioWorkletNode | null>(null);
  const denoiseRef = useRef<DenoiseChain | null>(null);
  const startingRef = useRef(false);

  // Read inside the worklet callback, which is created once — a state read there
  // would be frozen at whatever mute state capture started with.
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;
  const sendFrameRef = useRef(sendFrame);
  sendFrameRef.current = sendFrame;

  const stop = useCallback(() => {
    startingRef.current = false;
    workletRef.current?.port.close();
    workletRef.current?.disconnect();
    workletRef.current = null;
    denoiseRef.current?.destroy();
    denoiseRef.current = null;
    sourceRef.current?.disconnect();
    sourceRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setMediaStream(null);
    const ctx = ctxRef.current;
    ctxRef.current = null;
    if (ctx && ctx.state !== 'closed') ctx.close().catch(() => {});
    setStatus('idle');
  }, []);

  const start = useCallback(async () => {
    if (startingRef.current || ctxRef.current) return;
    startingRef.current = true;
    setStatus('starting');
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          // Echo cancellation on: the narration is coming out of the same laptop
          // speakers the mic is pointed at. Noise suppression on: until a denoiser
          // of ours is running, the browser is the only one (it is switched off
          // below once DeepFilterNet3 takes over, so two models never stack).
          echoCancellation: true,
          noiseSuppression: true,
          // AGC off: it rides the gain up while the customer is silent, which is
          // exactly when the agent is talking, so residual speaker echo gets
          // amplified past what the browser's AEC removed and the server's turn
          // detector hears the agent's own voice.
          autoGainControl: false,
          channelCount: 1,
          // RNNoise and DeepFilterNet3 are trained on 48 kHz audio; the downsampler
          // worklet brings the result down to the 16 kHz the socket expects.
          sampleRate: CAPTURE_SAMPLE_RATE,
          // `latency` is gone from newer lib.dom typings but Chromium still honours it.
          latency: 0
        } as MediaTrackConstraints
      });
      if (!startingRef.current) {
        // stop() was called while the permission prompt was up.
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;

      const AudioCtor = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new AudioCtor({ latencyHint: 'interactive', sampleRate: CAPTURE_SAMPLE_RATE });
      ctxRef.current = ctx;
      await ctx.audioWorklet.addModule(WORKLET_URL);
      if (!startingRef.current) return;

      const source = ctx.createMediaStreamSource(stream);
      // Split out of the main bundle: the LiveKit path never loads any of it.
      let tail: AudioNode = source;
      try {
        const { createDenoiseChain } = await import('./socketDenoise');
        if (!startingRef.current) return;
        const chain = await createDenoiseChain(ctx, source, (kind: DenoiseKind) => {
          if (kind !== 'deepfilternet3') return;
          // DeepFilterNet3 is running; take the browser's suppression off so the
          // two models do not stack. Best effort — not every browser allows it live.
          streamRef.current
            ?.getAudioTracks()[0]
            ?.applyConstraints({ noiseSuppression: false })
            .catch(() => {});
        });
        if (!startingRef.current) {
          chain.destroy();
          return;
        }
        denoiseRef.current = chain;
        tail = chain.output;
      } catch {
        // No denoiser: the raw capture still goes up, behind the browser's suppression.
      }
      const node = new AudioWorkletNode(ctx, 'pcm-downsampler', { numberOfInputs: 1, numberOfOutputs: 0 });
      node.port.onmessage = (event) => {
        if (isMutedRef.current) return;
        sendFrameRef.current(event.data as ArrayBuffer);
      };
      tail.connect(node);
      sourceRef.current = source;
      workletRef.current = node;

      if (ctx.state === 'suspended') await ctx.resume().catch(() => {});
      setMediaStream(stream);
      setStatus('live');
      startingRef.current = false;
    } catch (error) {
      startingRef.current = false;
      stop();
      const denied = isPermissionError(error);
      setStatus(denied ? 'denied' : 'error');
      setMicError(
        denied
          ? 'Microphone access is blocked — allow it in your browser to talk to the presenter.'
          : 'The microphone could not be started.'
      );
    }
  }, [stop]);

  useEffect(() => stop, [stop]);

  return { start, stop, status, micError, mediaStream };
}
